import { useState, useCallback } from 'react';
import { directUploadFiles, validateFileForUpload } from '../utils/directUpload';

/**
 * Custom hook for handling direct file uploads to Cloud Storage
 * @param {string} token - Authentication token
 * @returns {Object} - Hook interface
 */
export const useDirectUpload = (token) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState(null);

  /**
   * Upload files directly to Cloud Storage
   * @param {Array} files - Array of {file: File, fieldName: string} objects
   * @returns {Promise<Object>} - Upload results organized by field name
   */
  const uploadFiles = useCallback(async (files) => {
    if (!token) {
      throw new Error('Authentication token is required');
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress({});

    try {
      // Validate all files first
      const validationErrors = [];
      files.forEach(({ file, fieldName }, index) => {
        const validation = validateFileForUpload(file, fieldName);
        if (!validation.isValid) {
          validationErrors.push(`File ${index + 1} (${file.name}): ${validation.errors.join(', ')}`);
        }
      });

      if (validationErrors.length > 0) {
        throw new Error(`Validation failed:\n${validationErrors.join('\n')}`);
      }

      // Track progress for each file
      const handleProgress = (fileIndex, progress) => {
        setUploadProgress(prev => ({
          ...prev,
          [fileIndex]: progress
        }));
      };

      const result = await directUploadFiles(files, token, handleProgress);
      
      setIsUploading(false);
      setUploadProgress({});
      
      return result;

    } catch (error) {
      setIsUploading(false);
      setUploadError(error.message);
      setUploadProgress({});
      throw error;
    }
  }, [token]);

  /**
   * Reset the upload state
   */
  const resetUpload = useCallback(() => {
    setIsUploading(false);
    setUploadProgress({});
    setUploadError(null);
  }, []);

  /**
   * Get overall upload progress percentage
   */
  const getOverallProgress = useCallback(() => {
    const progresses = Object.values(uploadProgress);
    if (progresses.length === 0) return 0;
    const total = progresses.reduce((sum, progress) => sum + progress, 0);
    return Math.round(total / progresses.length);
  }, [uploadProgress]);

  /**
   * Get progress for a specific file
   */
  const getFileProgress = useCallback((fileIndex) => {
    return uploadProgress[fileIndex] || 0;
  }, [uploadProgress]);

  return {
    // State
    isUploading,
    uploadProgress,
    uploadError,
    
    // Actions
    uploadFiles,
    resetUpload,
    
    // Computed
    getOverallProgress,
    getFileProgress
  };
};

export default useDirectUpload;
